import {
	all,
	select,
	takeEvery,
	fork,
	StrictEffect,
	call,
	put,
	delay,
} from 'redux-saga/effects'
import packageJson from '../../../../package.json'

// action
import {
	authActions,
	userActions,
	configActions,
	mainActions,
	departmentActions,
	routeActions,
} from '@redux/store/index'

// lib
import { persistor } from '@redux/index'
import { firstMainTap, firstAdminTap } from '@redux/store/core/config.store'
import { Logger } from '@utils'
import { AccessControlManager } from '@guards'

// api
import { authApi, ResType, ParameterType } from '@api'

// schema
import {
	User,
	AccessControl,
	UserType,
	AccessControlType,
	AccessControlDbType,
} from '@schemas'

// type
import type {
	RootState,
	AuthStateType,
	ConfigStateType,
	UserMenuAccessType,
	TabType,
} from '@redux/types'

const apiLogin = async (data: ParameterType): Promise<ResType | undefined> => {
	const res: ResType | undefined = await authApi.login(data)
	return res
}

const apiTokenValidation = async (): Promise<ResType | undefined> => {
	const res: ResType | undefined = await authApi.tokenValidation()
	return res
}

const purgeStoredState = async (isRedirect: boolean): Promise<void> => {
	await persistor.purge().then(() => {
		if (isRedirect) {
			window.location.reload()
		}
	})
}

const apiLoginUsingAccessToken = async (
	accessToken: string,
): Promise<ResType | undefined> => {
	const customHeaders = { Authorization: `Bearer ${accessToken}` }
	const body = { accessToken: accessToken }
	const res: ResType | undefined = await authApi.loginUsingAccessToken({
		customHeaders: customHeaders,
		body: body,
	})

	return res
}

function* initLoginPage() {
	try {
		const isLogin: boolean = yield select(state => state.auth.isLogin)

		if (!isLogin) {
			yield put(authActions.IS_LOGIN(false))
			yield put(authActions.LOGIN_ERROR_MESSAGE(''))
			yield call(purgeStoredState, true)
		}
	} catch (error) {
		Logger.warn(error)
	}
}

function* tryLoginSaga(): Generator<StrictEffect, void> {
	try {
		const loginAccount: AuthStateType['login'] | any = yield select(
			(state: RootState) => state.auth.login as AuthStateType['login'],
		)

		const body = {
			email: loginAccount.email,
			password: loginAccount.password,
		}
		const apiRes: ResType | any = yield call(apiLogin, { body: { ...body } })

		if (apiRes.status === 201) {
			const data = apiRes.data[0]

			const token = { accessToken: data.accessToken, refreshToken: data.refreshToken }
			const user = new User({ ...data })
			const accessControlsData: AccessControlDbType[] = data['accessControls']
			const accessControls: AccessControlType[] = accessControlsData.map(
				(anAccess: any) => {
					return new AccessControl({
						...anAccess,
						menuI18n: anAccess.menuI18n ?? [],
					})
				},
			)

			yield put(authActions.TOKEN(token))
			yield put(authActions.LOGIN_ERROR_MESSAGE(''))
			yield put(userActions.USER(user))
			yield put(authActions.ACCESS_CONTROLS(accessControls))
			yield put(authActions.IS_LOGIN(true))
		} else if (apiRes.status === 0) {
			yield put(authActions.LOGIN_ERROR_MESSAGE('서버와 연결이 되지 않았습니다.'))
		} else {
			yield put(authActions.LOGIN_ERROR_MESSAGE('아이디와 패스워드를 다시 확인해주세요.'))
		}
	} catch (error) {
		Logger.warn(error)
	}
}

function* runLoginUsingAccessToken(): Generator<StrictEffect, void> {
	try {
		const data: AuthStateType['loginUsingAccessToken'] | any = yield select(
			(state: RootState) => state.auth.loginUsingAccessToken,
		)
		const accessToken: string = data !== undefined ? (data.accessToken as string) : ''

		yield call(purgeStoredState, false)
		yield put(configActions.IS_LOADING(true))

		const apiRes: ResType | any = yield call(apiLoginUsingAccessToken, accessToken)

		if (apiRes.status === 201) {
			const data = apiRes.data[0]
			const token = { accessToken: data.accessToken, refreshToken: data.refreshToken }
			const user = new User({ ...data })
			const accessControlsData: AccessControlDbType[] = data['accessControls']
			const accessControls: AccessControlType[] = accessControlsData.map(
				(anAccess: any) => {
					return new AccessControl({
						...anAccess,
						menuI18n: anAccess.menuI18n ?? [],
					})
				},
			)

			yield put(authActions.TOKEN(token))
			yield put(authActions.LOGIN_ERROR_MESSAGE(''))
			yield put(userActions.USER(user))
			yield put(authActions.ACCESS_CONTROLS(accessControls))
			yield put(authActions.IS_LOGIN(true))
		} else {
			yield put(authActions.IS_LOGIN(false))
			yield call(purgeStoredState, true)
		}
	} catch (error) {
		Logger.warn(error)
	}
}

function* isLoginSaga() {
	try {
		const isLogin: boolean = yield select(state => state.auth.isLogin)
		const mode: ConfigStateType['mode'] = yield select(state => state.config.mode)
		const defaultTab: TabType = mode === 'main' ? firstMainTap : firstAdminTap

		localStorage.setItem('isLogin', isLogin.toString())

		if (isLogin === true) {
			// login
			const user: UserType = yield select(state => state.user.user)
			const accessToken: string = localStorage.getItem('accessToken') ?? ''

			yield put(departmentActions.TRIGGER_GET_DEPARTMENT_LIST(true))
			yield put(mainActions.TRIGGER_GET_MENU(true))

			if (user.id === '' && accessToken !== '') {
				// fail
				yield put(authActions.LOGIN_USING_accessToken({ accessToken: accessToken }))
			} else if (user.id === '' && accessToken === '') {
				yield put(authActions.TRIGGER_LOGOUT(true))
			}
		} else {
			// logout
			yield call(purgeStoredState, false)

			if (mode !== 'main') {
				yield put(configActions.MODE('main'))
			}

			yield put(authActions.TOKEN({ accessToken: '', refreshToken: '' }))
			yield put(configActions.SELECTED_TAB_IDX('1'))
			yield put(configActions.TAB_LIST([defaultTab]))
			yield put(userActions.LANGUAGE(''))
		}
	} catch (error) {
		Logger.warn(error)
	}
}

function* runLogout() {
	try {
		const triggerLogin: boolean = yield select(state => state.auth.triggerLogin)

		if (triggerLogin) {
			yield put(authActions.IS_LOGIN(false))
			yield call(purgeStoredState, true)
			yield put(authActions.TRIGGER_LOGOUT(false))
		}
	} catch (error) {
		Logger.warn(error)
	}
}

function* checkAccessTokenSaga() {
	try {
		const localVersion = localStorage.getItem('systemVersion')
		if (localVersion === null || packageJson.version !== localVersion) {
			localStorage.setItem('systemVersion', packageJson.version)
			yield call(purgeStoredState, true)
		}

		const apiRes: ResType = yield call(apiTokenValidation)

		if (apiRes.status !== 201) {
			yield put(authActions.IS_LOGIN(false))
		} else {
			const accessControls: AccessControlType[] = yield select(
				state => state.auth.accessControls,
			)

			const isNotTheSamePrevAccess: boolean =
				JSON.stringify(accessControls) !== JSON.stringify(apiRes.data[0].accessControls)

			if (isNotTheSamePrevAccess) {
				yield put(authActions.ACCESS_CONTROLS(accessControls))
			}
		}
	} catch (error) {
		Logger.warn(error)
	}
}

function* authLoadingPageSaga() {
	try {
		const isLogin: boolean = yield select(state => state.auth.isLogin)
		const accessToken = localStorage.getItem('accessToken') ?? ''
		const apiRes: ResType = yield call(apiLoginUsingAccessToken, accessToken)

		if (apiRes.status === 201) {
			const data = apiRes.data[0]
			const token = { accessToken: data.accessToken, refreshToken: data.refreshToken }
			const user = new User({ ...(data as any) })
			const accessControlsData: AccessControlDbType[] = data['accessControls']
			const accessControls: AccessControlType[] = accessControlsData.map(
				(anAccess: any) => {
					return new AccessControl({
						...anAccess,
						menuI18n: anAccess.menuI18n ?? [],
					})
				},
			)

			yield put(authActions.TOKEN(token))
			yield put(authActions.LOGIN_ERROR_MESSAGE(''))
			yield put(userActions.USER(user))
			yield put(authActions.ACCESS_CONTROLS(accessControls))
			if (!isLogin) {
				yield put(authActions.IS_LOGIN(true))
			}
			delay(1000)
			yield put(routeActions.NAVIGATE_PATH('/main/home'))
		} else {
			yield put(authActions.IS_LOGIN(false))
			delay(1000)
			yield put(routeActions.NAVIGATE_PATH('/auth/login'))
		}
	} catch (error) {
		Logger.warn(error)
	}
}

function* accessControlsSaga() {
	try {
		const accessControls: AccessControlType[] = yield select(
			state => state.auth.accessControls,
		)

		const newUserAccessMenuList: UserMenuAccessType[] =
			AccessControlManager.getUserMenuAccessList(accessControls)

		yield put(authActions.USER_ACCESS_MENU_LIST(newUserAccessMenuList))
	} catch (error) {
		Logger.warn(error)
	}
}

function* mainSaga() {
	// trigger
	yield takeEvery(authActions.TRIGGER_INIT_LOGIN_PAGE, initLoginPage)
	yield takeEvery(authActions.TRIGGER_LOGOUT, runLogout)
	yield takeEvery(authActions.TRIGGER_CHECK_ACCESS_TOKEN, checkAccessTokenSaga)
	yield takeEvery(authActions.TRIGGER_AUTH_LOADING_PAGE, authLoadingPageSaga)

	// login
	yield takeEvery(authActions.IS_LOGIN, isLoginSaga)
	yield takeEvery(authActions.LOGIN, tryLoginSaga)

	// token
	yield takeEvery(authActions.LOGIN_USING_accessToken, runLoginUsingAccessToken)

	// access controls
	yield takeEvery(authActions.ACCESS_CONTROLS, accessControlsSaga)
}

export default function* rootSaga() {
	yield all([fork(mainSaga)])
}
